import { Transaction } from '../../services/types';

/**
 * What the ticket is for the client once the payment screen is behind them. Several technical
 * states mean the same thing on the page, and the screen follows the stage, not the raw status.
 *
 * `FILES_VERIFICATION` is a payment under review: the receipt is in, the money is not credited and
 * a dispute may still be opened. It is never a success.
 */
export type GateStage =
  | 'reupload'
  | 'receipt-check'
  | 'receipt-unconfirmed'
  | 'processing'
  | 'success'
  | 'declined'
  | 'expired';

export interface GateStageContext {
  /** The client confirmed the payment in this page session. */
  confirmSent: boolean;
  /** A re-uploaded receipt has not been picked up by the poll yet. */
  reuploading: boolean;
  /** The ticket left review for a requisite status and the next poll has not confirmed it. */
  reviewRegressHeld: boolean;
}

export const REQUISITE_STATUSES = ['WAITING_FOR_SMS', 'PAYMENT_PENDING'];

export const resolveGateStage = (
  transaction: Pick<Transaction, 'status' | 'receiptVerification'>,
  { confirmSent, reuploading, reviewRegressHeld }: GateStageContext,
): GateStage | null => {
  const { status, receiptVerification } = transaction;

  switch (status) {
    case 'COMPLETED':
      return 'success';
    case 'FILES_VERIFICATION':
      return receiptVerification?.phase === 'unconfirmed' ? 'receipt-unconfirmed' : 'processing';
    case 'FILES_AUTO_VERIFICATION':
      return receiptVerification?.phase === 'failed' && receiptVerification.canReupload && !reuploading
        ? 'reupload'
        : 'receipt-check';
    case 'DECLINED':
      return 'declined';
    case 'EXPIRED':
      return confirmSent ? 'declined' : 'expired';
    default:
      return REQUISITE_STATUSES.includes(status) && (confirmSent || reviewRegressHeld)
        ? 'processing'
        : null;
  }
};
